import {
  GameProgress,
  DEFAULT_GAME_PROGRESS,
  isValidGameProgress,
} from '../types/GameProgress';
import { NORMAL_MONSTER_IDS } from '../constants/monsters';
import { AILevel } from '../ai/types';

const SHUKUREN_LEVELS: readonly AILevel[] = [AILevel.LV5, AILevel.LV6];

/** LocalStorageのキー */
export const PROGRESS_STORAGE_KEY = 'monster-buttle-progress';

/**
 * ゲーム進捗をLocalStorageから読み込む
 * データがない場合や壊れている場合はデフォルト値を返す
 */
export function loadGameProgress(): GameProgress {
  try {
    const stored = localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_GAME_PROGRESS };

    const parsed = JSON.parse(stored);
    if (!isValidGameProgress(parsed)) return { ...DEFAULT_GAME_PROGRESS };

    // 旧セーブデータの後方互換
    if (!Array.isArray(parsed.bossDefeatedWith)) parsed.bossDefeatedWith = [];
    if (!Array.isArray(parsed.unlockedHiddenMonsters)) parsed.unlockedHiddenMonsters = [];
    if (typeof parsed.tournamentWonAtShukuren !== 'boolean') parsed.tournamentWonAtShukuren = false;
    if (typeof parsed.anyTournamentWon !== 'boolean') parsed.anyTournamentWon = false;
    return parsed;
  } catch {
    return { ...DEFAULT_GAME_PROGRESS };
  }
}

/**
 * ゲーム進捗をLocalStorageに保存する
 */
export function saveGameProgress(progress: GameProgress): void {
  try {
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // LocalStorageが使えない場合は無視
  }
}

/**
 * ボス撃破時に使用したキャラIDを記録する（重複なし）
 */
export function updateBossDefeatedWith(monsterIds: string[]): GameProgress {
  const current = loadGameProgress();
  const merged = Array.from(new Set([...(current.bossDefeatedWith ?? []), ...monsterIds]));
  if (merged.length !== (current.bossDefeatedWith ?? []).length) {
    current.bossDefeatedWith = merged;
    saveGameProgress(current);
  }
  return current;
}

/**
 * 隠しキャラクターが解放済みか確認する
 */
export function isHiddenMonsterUnlocked(id: string): boolean {
  const progress = loadGameProgress();
  return progress.unlockedHiddenMonsters.includes(id);
}

/**
 * 隠しキャラクターを解放する（重複時はスキップ）
 */
export function unlockHiddenMonster(id: string): GameProgress {
  const current = loadGameProgress();
  if (current.unlockedHiddenMonsters.includes(id)) return current;
  current.unlockedHiddenMonsters = [...current.unlockedHiddenMonsters, id];
  saveGameProgress(current);
  return current;
}

/**
 * 全通常魂格でボスを撃破済みか確認する（モココ解放条件1）
 */
export function hasDefeatedBossWithAllNormalSpecies(): boolean {
  const { bossDefeatedWith } = loadGameProgress();
  const defeated = new Set(bossDefeatedWith);
  return NORMAL_MONSTER_IDS.every((id) => defeated.has(id));
}

/**
 * 熟練以上の大会で優勝済みか確認する（アリス解放条件2）
 */
export function hasTournamentWonAtShukuren(): boolean {
  return loadGameProgress().tournamentWonAtShukuren;
}

/**
 * 大会優勝を記録する。熟練以上（LV5/LV6）の場合のみフラグをONにする
 */
export function recordTournamentWin(difficulty: AILevel): GameProgress {
  const current = loadGameProgress();
  if (!SHUKUREN_LEVELS.includes(difficulty)) return current;
  if (current.tournamentWonAtShukuren) return current;
  current.tournamentWonAtShukuren = true;
  saveGameProgress(current);
  return current;
}

/**
 * 難易度不問で大会優勝を記録する（アリスヒント用）
 */
export function recordAnyTournamentWin(): GameProgress {
  const current = loadGameProgress();
  if (current.anyTournamentWon) return current;
  current.anyTournamentWon = true;
  saveGameProgress(current);
  return current;
}

/**
 * ステージクリア時に進捗を更新する
 * 現在のclearedStagesより大きい場合のみ更新する
 */
export function updateClearedStages(stageNumber: number): GameProgress {
  const current = loadGameProgress();
  if (stageNumber > current.clearedStages) {
    current.clearedStages = stageNumber;
    saveGameProgress(current);
  }
  return current;
}
